/**
 * Admin auth helper — attaches Bearer token, handles 401 by clearing session
 * and forcing a redirect to /admin/login.
 *
 * Used by ALL /api/admin/* calls from the frontend.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ADMIN_TOKEN_KEY = "zenme_admin_token";
export const ADMIN_TOKEN_EXP_KEY = "zenme_admin_token_exp";

export const getAdminToken = () => {
    try {
        return window.localStorage.getItem(ADMIN_TOKEN_KEY) || null;
    } catch (e) {
        return null;
    }
};

export const setAdminToken = (token, expiresAt) => {
    try {
        window.localStorage.setItem(ADMIN_TOKEN_KEY, token);
        if (expiresAt) {
            window.localStorage.setItem(ADMIN_TOKEN_EXP_KEY, expiresAt);
        }
    } catch (e) {
        /* ignore */
    }
};

export const clearAdminToken = () => {
    try {
        window.localStorage.removeItem(ADMIN_TOKEN_KEY);
        window.localStorage.removeItem(ADMIN_TOKEN_EXP_KEY);
    } catch (e) {
        /* ignore */
    }
};

export const isAdminTokenExpired = () => {
    try {
        const exp = window.localStorage.getItem(ADMIN_TOKEN_EXP_KEY);
        if (!exp) return false;
        return new Date(exp).getTime() <= Date.now();
    } catch (e) {
        return false;
    }
};

export const adminFetch = async (path, options = {}) => {
    const token = getAdminToken();
    const headers = {
        "Content-Type": "application/json",
        ...(options.headers || {}),
    };
    if (token) headers.Authorization = `Bearer ${token}`;

    const resp = await fetch(`${API}${path}`, {
        ...options,
        headers,
    });

    if (resp.status === 401) {
        clearAdminToken();
        if (window.location.pathname !== "/admin/login") {
            window.location.replace("/admin/login");
        }
        throw new Error("Unauthorized");
    }

    const contentType = resp.headers.get("content-type") || "";
    const data = contentType.includes("application/json")
        ? await resp.json().catch(() => null)
        : await resp.text();

    if (!resp.ok) {
        const err = new Error(
            (data && data.detail) ||
                (typeof data === "string" && data) ||
                `HTTP ${resp.status}`,
        );
        err.status = resp.status;
        err.body = data;
        throw err;
    }
    return data;
};

export default adminFetch;
